import Cookies from 'js-cookie';
import { API_URL, ENVIRONMENT } from 'settings';

export const monitoringEnabled =
  process.env.REACT_APP_MONITORING_ENABLED === 'true';

type Observation = {
  kind: string;
  client_at: string;
  session_id?: number;
  person_id?: number;
  duration_ms?: number;
  selection?: 'manual' | 'automatic' | 'restored' | 'changed';
  engine?: 'legacy' | 'fast';
  page?: string;
  join_id?: string;
};

let pending: Observation[] = [];
let pendingToken: string | undefined;
let timer: ReturnType<typeof setTimeout> | undefined;
let sending = false;
const joins = new Map<
  number,
  {
    started: number;
    id: string;
    ready: boolean;
    retrieved: boolean;
    failed: boolean;
  }
>();
export const BOOTSTRAP_RECEIVED = 'pm-monitoring-bootstrap-received';

// Separate transport: never await business requests, refresh auth, retry, or show errors.
export function flushMonitoring(keepalive = false): void {
  if (!monitoringEnabled) return;
  try {
    if (timer) clearTimeout(timer);
    timer = undefined;
    if (sending || !pending.length) return;
    const token = pendingToken;
    const events = pending.splice(0, 20);
    if (!token) return;
    sending = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);
    void fetch(`${API_URL}/monitoring/collect/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ events }),
      signal: controller.signal,
      keepalive,
    })
      .catch(() => undefined)
      .finally(() => {
        clearTimeout(timeout);
        sending = false;
        if (pending.length && !timer)
          timer = setTimeout(flushMonitoring, 10000);
      });
  } catch (_) {
    sending = false;
    pending = [];
  }
}

export function monitor(
  kind: string,
  data: Omit<Partial<Observation>, 'kind' | 'client_at'> = {},
): void {
  if (!monitoringEnabled) return;
  try {
    const token = Cookies.get(`pm_${ENVIRONMENT}_token`);
    if (!token) return;
    // Never attribute queued observations to a later account on this browser.
    if (pendingToken !== token) pending = [];
    pendingToken = token;
    if (pending.length < 40)
      pending.push({ ...data, kind, client_at: new Date().toISOString() });
    if (!timer) timer = setTimeout(flushMonitoring, 10000);
  } catch (_) {
    /* Monitoring cannot interrupt a reducer, socket, or UI handler. */
  }
}

export function beginJoin(sessionId: number): void {
  if (!monitoringEnabled) return;
  try {
    joins.clear();
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    joins.set(sessionId, {
      started: performance.now(),
      id,
      ready: false,
      retrieved: false,
      failed: false,
    });
    monitor('session.join_started', { session_id: sessionId, join_id: id });
  } catch (_) {}
}

export function readyJoin(sessionId: number, engine: 'legacy' | 'fast'): void {
  if (!monitoringEnabled) return;
  try {
    const join = joins.get(sessionId);
    if (
      !join ||
      join.ready ||
      join.failed ||
      (engine === 'legacy' && !join.retrieved)
    )
      return;
    join.ready = true;
    monitor('session.ready', {
      session_id: sessionId,
      engine,
      join_id: join.id,
      duration_ms: Math.max(0, performance.now() - join.started),
    });
  } catch (_) {}
}

export function bootstrapMetadata(
  sessionId: number,
  trigger?: 'initial' | 'reconnect' | 'resync',
) {
  if (!monitoringEnabled) return undefined;
  try {
    const join = joins.get(sessionId);
    return join
      ? {
          join_id: join.id,
          trigger: trigger || (join.ready ? 'reconnect' : 'initial'),
        }
      : undefined;
  } catch (_) {
    return undefined;
  }
}

// Only the response belonging to this visit may release legacy readiness.
// Cached Redux session data and late responses from an older visit cannot do so.
export function legacyRetrieved(sessionId: number, joinId: string) {
  if (!monitoringEnabled) return;
  try {
    const join = joins.get(sessionId);
    if (!join || join.id !== joinId || join.retrieved) return;
    join.retrieved = true;
    window.dispatchEvent(
      new CustomEvent(BOOTSTRAP_RECEIVED, { detail: sessionId }),
    );
  } catch (_) {}
}

export function failedJoin(sessionId: number, joinId?: string) {
  if (!monitoringEnabled) return;
  try {
    const join = joins.get(sessionId);
    if (!join || join.id !== joinId || join.failed || join.ready) return;
    join.failed = true;
    monitor('session.join_failed', {
      session_id: sessionId,
      join_id: join.id,
      duration_ms: Math.max(0, performance.now() - join.started),
    });
  } catch (_) {}
}
